import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMarketing, Campaign } from "../../../context/MarketingContext";
import { Label } from "../../../components/ui/Label";
import { Input } from "../../../components/ui/input2";
import { Textarea } from "../../../components/ui/Textarea";
import { Button } from "../../../components/ui/Button";
import { X } from "lucide-react";
import { formatApiErrors } from "../../../services/marketingTemplatesApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useToast } from "../../../hooks/useToast";

const NONE_OPTION = "__none__";

interface CampaignFormProps {
  campaign?: Campaign | null;
}

export const CampaignForm: React.FC<CampaignFormProps> = ({ campaign }) => {
  const isEdit = Boolean(campaign);
  const navigate = useNavigate();
  const toast = useToast();
  const {
    customers,
    customersLoading,
    emailTemplates,
    smsTemplates,
    calculateCreditsNeeded,
    remainingCredits,
    addCampaign,
    updateCampaign,
  } = useMarketing();

  const [name, setName] = useState(campaign?.name ?? "");
  const [description, setDescription] = useState(campaign?.description ?? "");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>(
    campaign?.selectedCustomerIds ?? [],
  );
  const [useAllCustomers, setUseAllCustomers] = useState(campaign?.useAllCustomers ?? false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [tempSelectedCustomerIds, setTempSelectedCustomerIds] = useState<string[]>(
    campaign?.selectedCustomerIds ?? [],
  );
  const [tempUseAllCustomers, setTempUseAllCustomers] = useState(
    campaign?.useAllCustomers ?? false,
  );
  const [customerSearch, setCustomerSearch] = useState("");
  const [emailTemplateId, setEmailTemplateId] = useState<string | undefined | null>(
    campaign?.emailTemplateId ?? undefined,
  );
  const [smsTemplateId, setSmsTemplateId] = useState<string | undefined | null>(
    campaign?.smsTemplateId ?? undefined,
  );

  const [errors, setErrors] = useState<{
    name?: string;
    description?: string;
    customers?: string;
    channels?: string;
  }>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(campaign?.name ?? "");
    setDescription(campaign?.description ?? "");
    setSelectedCustomerIds(campaign?.selectedCustomerIds ?? []);
    setUseAllCustomers(campaign?.useAllCustomers ?? false);
    setTempSelectedCustomerIds(campaign?.selectedCustomerIds ?? []);
    setTempUseAllCustomers(campaign?.useAllCustomers ?? false);
    setEmailTemplateId(campaign?.emailTemplateId ?? undefined);
    setSmsTemplateId(campaign?.smsTemplateId ?? undefined);
  }, [
    campaign?.id,
    campaign?.name,
    campaign?.description,
    campaign?.selectedCustomerIds,
    campaign?.useAllCustomers,
    campaign?.emailTemplateId,
    campaign?.smsTemplateId,
  ]);

  const creditsNeeded = useMemo(() => {
    if (!customers.length) return 0;
    return calculateCreditsNeeded({
      selectedCustomerIds,
      useAllCustomers,
      emailTemplateId: emailTemplateId || undefined,
      smsTemplateId: smsTemplateId || undefined,
    });
  }, [
    calculateCreditsNeeded,
    customers.length,
    selectedCustomerIds,
    useAllCustomers,
    emailTemplateId,
    smsTemplateId,
  ]);

  const remainingAfter = remainingCredits - creditsNeeded;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (!name.trim()) {
      nextErrors.name = "Campaign name is required.";
    }
    if (!description.trim()) {
      nextErrors.description = "Description is required.";
    }
    if (!useAllCustomers && selectedCustomerIds.length === 0) {
      nextErrors.customers = "Select at least one customer or choose All customers.";
    }
    if (!emailTemplateId && !smsTemplateId) {
      nextErrors.channels = "Select at least one template (email or SMS).";
    }
    if (remainingAfter < 0) {
      nextErrors.channels = "Insufficient credits for this audience and channel selection.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSaving(true);
      if (isEdit && campaign) {
        await updateCampaign(campaign.id, {
          name: name.trim(),
          description: description.trim(),
          selectedCustomerIds,
          useAllCustomers,
          emailTemplateId: emailTemplateId ?? null,
          smsTemplateId: smsTemplateId ?? null,
        });
        toast.success("Campaign updated.");
      } else {
        await addCampaign({
          name: name.trim(),
          description: description.trim(),
          selectedCustomerIds,
          useAllCustomers,
          emailTemplateId: emailTemplateId ?? null,
          smsTemplateId: smsTemplateId ?? null,
        });
        toast.success("Campaign created.");
      }
      navigate("/marketing/campaigns");
    } catch (err) {
      toast.error(formatApiErrors(err));
    } finally {
      setSaving(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((customer) => {
      const haystack = `${customer.name} ${customer.email || ""} ${customer.mobile || ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [customers, customerSearch]);

  const toggleCustomer = (customerId: string) => {
    setTempSelectedCustomerIds((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId],
    );
  };

  const openCustomerModal = () => {
    setTempSelectedCustomerIds(selectedCustomerIds);
    setTempUseAllCustomers(useAllCustomers);
    setCustomerSearch("");
    setCustomerModalOpen(true);
  };

  const applyCustomerSelection = () => {
    setSelectedCustomerIds(tempSelectedCustomerIds);
    setUseAllCustomers(tempUseAllCustomers);
    setCustomerModalOpen(false);
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="max-w-3xl mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-5"
      >
        <div className="space-y-1.5">
          <Label htmlFor="campaign-name">Campaign Name</Label>
          <Input
            id="campaign-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. January Promo"
          />
          {errors.name && (
            <p className="text-xs text-red-500 mt-1">{errors.name}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="campaign-description">Description</Label>
          <Textarea
            id="campaign-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Internal summary of this campaign."
            resize="vertical"
            error={errors.description}
          />
        </div>

        <div className="space-y-2">
          <Label>Customers</Label>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">
                {useAllCustomers
                  ? `All customers selected (${customers.length})`
                  : `${selectedCustomerIds.length} customer${selectedCustomerIds.length === 1 ? "" : "s"} selected`}
              </p>
              <p className="text-xs text-gray-500">
                {useAllCustomers
                  ? "Campaign will target every customer."
                  : "Open modal to search and select multiple customers."}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={openCustomerModal}
              className="whitespace-nowrap"
            >
              Select Customers
            </Button>
          </div>
          {errors.customers && (
            <p className="text-xs text-red-500 mt-1">{errors.customers}</p>
          )}
        </div>

        <div className="space-y-3">
          <Label>Templates</Label>
          <p className="text-xs text-gray-500 -mt-1">
            Select at least one channel; you can use email, SMS, or both.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Email Template (optional)
              </span>
              <Select
                value={emailTemplateId ?? NONE_OPTION}
                onValueChange={(value) =>
                  setEmailTemplateId(value === NONE_OPTION ? undefined : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select email template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_OPTION}>Select email template</SelectItem>
                  {emailTemplates.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                SMS Template (optional)
              </span>
              <Select
                value={smsTemplateId ?? NONE_OPTION}
                onValueChange={(value) =>
                  setSmsTemplateId(value === NONE_OPTION ? undefined : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select SMS template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_OPTION}>Select SMS template</SelectItem>
                  {smsTemplates.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {errors.channels && (
            <p className="text-xs text-red-500 mt-1">{errors.channels}</p>
          )}
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sm">
          <div>
            <p className="text-gray-600">
              Credits needed:{" "}
              <span className="font-semibold">{creditsNeeded.toFixed(2)}</span>
            </p>
            <p className="text-gray-500 text-xs">
              Remaining after save:{" "}
              <span
                className={
                  remainingAfter < 0 ? "text-red-600 font-semibold" : "font-semibold"
                }
              >
                {remainingAfter.toFixed(2)}
              </span>
            </p>
          </div>
          <div className="text-xs text-gray-500">
            Email and SMS costs are approximated per recipient for preview only.
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/marketing/campaigns")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
          >
            {saving ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save Changes" : "Create Campaign"}
          </Button>
        </div>
      </form>

      {customerModalOpen && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/45"
            onClick={() => setCustomerModalOpen(false)}
          />
          <div className="relative w-full max-w-3xl mx-4 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-base font-semibold text-[#402B69]">
                  Select Customers
                </h3>
                <p className="text-xs text-gray-500">
                  Choose multiple customers or switch to all customers.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCustomerModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={tempUseAllCustomers}
                  onChange={(e) => setTempUseAllCustomers(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                />
                All customers ({customers.length})
              </label>

              {!tempUseAllCustomers && (
                <>
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search customer by name, email, mobile..."
                    className="bg-white"
                  />

                  <div className="max-h-[360px] overflow-y-auto bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {customersLoading ? (
                      <div className="p-3 text-sm text-gray-500">Loading customers...</div>
                    ) : filteredCustomers.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500">No customers found.</div>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <label
                          key={customer.id}
                          className="flex items-center gap-3 p-3 hover:bg-purple-50/30 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={tempSelectedCustomerIds.includes(customer.id)}
                            onChange={() => toggleCustomer(customer.id)}
                            className="h-4 w-4 rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {customer.name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {customer.email || customer.mobile || "No contact info"}
                            </p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50">
              <p className="text-xs text-gray-600">
                {tempUseAllCustomers
                  ? `All customers selected (${customers.length})`
                  : `${tempSelectedCustomerIds.length} customer${tempSelectedCustomerIds.length === 1 ? "" : "s"} selected`}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCustomerModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
                  onClick={applyCustomerSelection}
                >
                  Apply Selection
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CampaignForm;

